import React, { useState, useEffect } from 'react';
import {
    User,
    Phone,
    Calendar,
    MapPin,
    Lock,
    Camera,
    ArrowLeft,
    UserPlus,
    Stethoscope,
    GraduationCap,
    Building2,
    MapPinned,
    CheckCircle,
    AlertCircle,
    Settings,
    FileText,
    Upload
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchAllData, fetchData, fetchUpdateData } from '../../helpers/externapi';
import Layout from '../../Layout/Layout';
import PageWrapper from '../../Components/PageWrapper';
import { fetchConfigValues } from '../../Commoncomponents/CommonComponents';

export default function AddUserToClinic({ mode = 'add' }) {
    const { smartClinicId, userId } = useParams();
    const navigate = useNavigate();
    const UserId = localStorage.getItem("UserId");

    // Form states
    const [formData, setFormData] = useState({
        fullName: '',
        gender: '',
        DateofBirth: '',
        age: '',
        mobileNumber: '',
        password: 1234,
        userImage: '',
        addressLine1: '',
        addressLine2: '',
        village: '',
        mandal: '',
        city: '',
        stateId: '',
        districtId: '',
        pincode: '',
        qualification: '',
        smartClinicId: smartClinicId,
        userRoleId: '',
        ReportingTo: UserId
    });

    // API data states
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [documentTypes] = useState([
        { KYCCardTypesId: '1', KYCCardType: 'Aadhar Card' },
        { KYCCardTypesId: '2', KYCCardType: 'PAN Card' }
    ]);

    // UI states
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [imagePreview, setImagePreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [document, setDocument] = useState({
        documentType: '',
        file: null,
        preview: null,
        documentImage: null,
        userDocumentsId: null
    });
    const [imageUrl, setImageUrl] = useState('');
    const [documentImageUrl, setDocumentImageUrl] = useState('');

    // Calculate age from date of birth
    const calculateAgeFromDOB = (dob) => {
        if (!dob) return '';
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age.toString();
    };

    // Calculate date of birth from age
    const calculateDOBFromAge = (age) => {
        if (!age || isNaN(age)) return '';
        const today = new Date();
        const birthYear = today.getFullYear() - parseInt(age);
        const dob = new Date(birthYear, 0, 1);
        return dob.toISOString().split('T')[0];
    };

    // Handle date of birth change
    const handleDOBChange = (value) => {
        handleInputChange('DateofBirth', value);
        if (value) {
            const calculatedAge = calculateAgeFromDOB(value);
            handleInputChange('age', calculatedAge);
        } else {
            handleInputChange('age', '');
        }
    };

    // Handle age change
    const handleAgeChange = (value) => {
        if (value && !/^\d*$/.test(value)) return;
        handleInputChange('age', value);
        if (value && !isNaN(value)) {
            const calculatedDOB = calculateDOBFromAge(value);
            handleInputChange('DateofBirth', calculatedDOB);
        } else {
            handleInputChange('DateofBirth', '');
        }
    };

    // Handle input change with validation
    const handleInputChange = (field, value) => {
        if ((field === 'mobileNumber' || field === 'pincode') && value && !/^\d*$/.test(value)) {
            return;
        }
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // Handle image upload
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result.split(',')[1];
                setFormData(prev => ({
                    ...prev,
                    userImage: base64String
                }));
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle document upload
    const handleDocumentUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result.split(',')[1];
                setDocument(prev => ({
                    ...prev,
                    file: file,
                    preview: reader.result,
                    documentImage: base64String
                }));
            };
            reader.readAsDataURL(file);
        } else {
            showNotification('Please upload an image file (Aadhar or PAN card)', 'error');
        }
    };

    // Handle document type change
    const handleDocumentTypeChange = (value) => {
        setDocument(prev => ({
            ...prev,
            documentType: value
        }));
    };

    // Fetch initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true);
                const config = await fetchConfigValues();
                setImageUrl(config.userImagesBucketlink || '');
                setDocumentImageUrl(config.userDocumentsBucketURL || '');

                const statesResponse = await fetchData('States/all', { skip: 0, take: 0 });
                setStates(statesResponse);
                const userRolesResponse = await fetchData('UserRoles/all', { skip: 0, take: 0 });
                setUserRoles(userRolesResponse);

                if (mode === 'edit' && userId) {
                    const userResponse = await fetchAllData(`lambdaAPI/User/GetById/${userId}`);
                    if (userResponse && userResponse[0]) {
                        setFormData({
                            fullName: userResponse[0].FullName || '',
                            gender: userResponse[0].Gender || '',
                            DateofBirth: userResponse[0].DateofBirth ? userResponse[0].DateofBirth.split("T")[0] : '',
                            age: userResponse[0].Age || '',
                            mobileNumber: userResponse[0].MobileNumber || '',
                            password: userResponse[0].Password || '',
                            userImage: userResponse[0].UserImage || '',
                            addressLine1: userResponse[0].AddressLine1 || '',
                            addressLine2: userResponse[0].AddressLine2 || '',
                            village: userResponse[0].Village || '',
                            mandal: userResponse[0].Mandal || '',
                            city: userResponse[0].City || '',
                            stateId: userResponse[0].StateId || '',
                            districtId: userResponse[0].DistrictId || '',
                            pincode: userResponse[0].Pincode || '',
                            qualification: userResponse[0].Qualification || '',
                            smartClinicId: userResponse[0].SmartClinicId || smartClinicId,
                            userRoleId: userResponse[0].UserRoleId || '',
                            ReportingTo: UserId
                        });

                        if (userResponse[0].UserImage) {
                            setImagePreview(`${config.userImagesBucketlink}${userResponse[0].UserImage}`);
                        }

                        if (userResponse[0].DocumentImage && userResponse[0].DocumentType) {
                            setDocument({
                                documentType: userResponse[0].DocumentType,
                                file: null,
                                preview: `${config.userDocumentsBucketURL}${userResponse[0].DocumentImage}`,
                                documentImage: userResponse[0].DocumentImage,
                                userDocumentsId: userResponse[0].UserDocumentsId
                            });
                        }
                    }
                }
            } catch (error) {
                showNotification('Failed to load initial data', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [mode, userId, smartClinicId]);

    // Fetch districts when state changes
    useEffect(() => {
        const fetchDistricts = async () => {
            if (formData.stateId) {
                try {
                    setLoading(true);
                    const response = await fetchAllData(`Districts/GetByStateId/${formData.stateId}`);
                    setDistricts(response);
                } catch (error) {
                    showNotification('Failed to load districts', 'error');
                } finally {
                    setLoading(false);
                }
            } else {
                setDistricts([]);
                setFormData(prev => ({ ...prev, districtId: '' }));
            }
        };

        fetchDistricts();
    }, [formData.stateId]);

    // Validate form
    const validateForm = () => {
        const requiredFields = [
            { key: 'fullName', type: 'input', label: 'Full Name' },
            { key: 'gender', type: 'select', label: 'Gender' },
            { key: 'DateofBirth', type: 'input', label: 'Date of Birth' },
            { key: 'mobileNumber', type: 'input', label: 'Mobile Number' },
            { key: 'userRoleId', type: 'select', label: 'User Role' },
            { key: 'addressLine1', type: 'input', label: 'Address Line 1' },
            { key: 'stateId', type: 'select', label: 'State' },
            { key: 'districtId', type: 'select', label: 'District' },
            { key: 'mandal', type: 'input', label: 'Mandal' },
            { key: 'city', type: 'input', label: 'City' },
            { key: 'village', type: 'input', label: 'Village' },
            { key: 'pincode', type: 'input', label: 'Pincode' },
        ];

        const newErrors = {};

        for (let field of requiredFields) {
            if (!formData[field.key]) {
                newErrors[field.key] = field.type === 'select'
                    ? `Please select ${field.label.toLowerCase()}`
                    : `Please enter ${field.label.toLowerCase()}`;
            }
        }

        if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
            newErrors.mobileNumber = 'Mobile number must be a 10-digit number';
        }
        if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) {
            newErrors.pincode = 'Pincode must be a 6-digit number';
        }
        if (formData.fullName && !/^[a-zA-Z\s]+$/.test(formData.fullName)) {
            newErrors.fullName = 'Full name should contain only letters and spaces';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Show notification
    const showNotification = (message, type = 'success') => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: '', type: 'success' });
        }, 4000);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showNotification('Please fix the errors in the form', 'error');
            return;
        }

        setLoading(true);

        try {
            const { userImage, ...formDataWithoutImage } = formData;
            let userIdToUse = userId;

            if (mode === 'add') {
                const userResponse = await fetchData('lambdaAPI/User/add', formDataWithoutImage);
                userIdToUse = userResponse.userId;

                if (userImage) {
                    await fetchData('lambdaAPI/User/imageupload', {
                        userId: userIdToUse,
                        userImage: userImage
                    });
                }

                showNotification('User added successfully!', 'success');
            } else {
                const updatePayload = [
                    { columnName: "FullName", columnValue: formData.fullName, tableName: "User", tableId: userId },
                    { columnName: "Gender", columnValue: formData.gender, tableName: "User", tableId: userId },
                    { columnName: "DateofBirth", columnValue: formData.DateofBirth, tableName: "User", tableId: userId },
                    { columnName: "MobileNumber", columnValue: formData.mobileNumber, tableName: "User", tableId: userId },
                    { columnName: "AddressLine1", columnValue: formData.addressLine1, tableName: "User", tableId: userId },
                    { columnName: "AddressLine2", columnValue: formData.addressLine2, tableName: "User", tableId: userId },
                    { columnName: "Village", columnValue: formData.village, tableName: "User", tableId: userId },
                    { columnName: "Mandal", columnValue: formData.mandal, tableName: "User", tableId: userId },
                    { columnName: "City", columnValue: formData.city, tableName: "User", tableId: userId },
                    { columnName: "StateId", columnValue: formData.stateId, tableName: "User", tableId: userId },
                    { columnName: "DistrictId", columnValue: formData.districtId, tableName: "User", tableId: userId },
                    { columnName: "Pincode", columnValue: formData.pincode, tableName: "User", tableId: userId },
                    { columnName: "Qualification", columnValue: formData.qualification, tableName: "User", tableId: userId },
                    { columnName: "UserRoleId", columnValue: formData.userRoleId, tableName: "User", tableId: userId }
                ];

                await fetchUpdateData("lambdaAPI/Update/CommonUpdate", updatePayload);

                if (userImage && typeof userImage === 'string' && userImage.startsWith('data:')) {
                    await fetchData('lambdaAPI/User/imageupload', {
                        userId: userIdToUse,
                        userImage: userImage
                    });
                }

                showNotification('User updated successfully!', 'success');
            }

            if ( document.documentType && document.documentImage) {
                if (mode === 'add') {
                    const addResponse = await fetchData('lambdaAPI/UserDocuments/add', {
                        UserId: userIdToUse,
                        DocumentType: document.documentType,
                        DocumentImage: document.documentImage
                    });

                    if (addResponse.data.userDocumentsId) {
                        await fetchData('lambdaAPI/UserDocuments/imageupload', {
                            UserDocumentsId: addResponse.data.userDocumentsId,
                            DocumentImage: document.documentImage
                        });
                        showNotification('Document uploaded successfully!', 'success');
                    }
                } else if (document.userDocumentsId && document.documentImage ) {
                    await fetchData('lambdaAPI/UserDocuments/imageupload', {
                        UserDocumentsId: document.userDocumentsId,
                        DocumentImage: document.documentImage
                    });
                    showNotification('Document updated successfully!', 'success');
                }
            }

            setTimeout(() => {
                navigate(`/smartclinics/details/${smartClinicId}`);
            }, 2000);
        } catch (error) {
            showNotification(`Failed to ${mode === 'add' ? 'add' : 'update'} user. Please try again.`, 'error');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get role icon
    const getRoleIcon = (roleId) => {
        switch (parseInt(roleId)) {
            case 1: return <Stethoscope style={{ width: '20px', height: '20px' }} />;
            case 2: return <User style={{ width: '20px', height: '20px' }} />;
            case 3: return <Building2 style={{ width: '20px', height: '20px' }} />;
            case 4: return <GraduationCap style={{ width: '20px', height: '20px' }} />;
            case 34: return <UserPlus style={{ width: '20px', height: '20px' }} />;
            case 35: return <Settings style={{ width: '20px', height: '20px' }} />;
            default: return <User style={{ width: '20px', height: '20px' }} />;
        }
    };

    // Required field indicator
    const RequiredField = () => <span style={{ color: '#ef4444' }}>*</span>;

    return (
        <Layout>
            <PageWrapper>
                <div style={{
                    minHeight: '100vh',
                    background: 'linear-gradient(to bottom right, #ebf8ff, #ffffff, #e0e7ff)'
                }}>
                    {/* Notification */}
                    {notification.show && (
                        <div style={{
                            position: 'fixed',
                            top: '16px',
                            right: '16px',
                            zIndex: 50,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            backgroundColor: notification.type === 'success' ? '#10b981' : '#ef4444',
                            color: 'white'
                        }}>
                            {notification.type === 'success' ? (
                                <CheckCircle style={{ width: '20px', height: '20px' }} />
                            ) : (
                                <AlertCircle style={{ width: '20px', height: '20px' }} />
                            )}
                            {notification.message}
                        </div>
                    )}

                    <div className="container mx-auto px-4 py-8" style={{ maxWidth: '1024px' }}>
                        {/* Header */}
                        <div style={{ marginBottom: '32px' }}>
                            <div className="d-flex align-items-center gap-3 mb-4">
                                <button
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                    onClick={() => navigate(`/smartclinics/details/${smartClinicId}`)}
                                    style={{ borderRadius: '12px' }}
                                >
                                    <ArrowLeft style={{ width: '16px', height: '16px' }} />
                                    Back to Clinic
                                </button>
                            </div>
                            <div className="card" style={{
                                borderRadius: '16px',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                border: '1px solid #f3f4f6',
                                padding: '32px'
                            }}>
                                <div className="d-flex align-items-center gap-4 mb-6">
                                    <div style={{
                                        width: '64px',
                                        height: '64px',
                                        background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                                        borderRadius: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <UserPlus style={{ width: '32px', height: '32px', color: 'white' }} />
                                    </div>
                                    <div>
                                        <h1 style={{ fontSize: '30px', fontWeight: 'bold', color: '#111827' }}>
                                            {mode === 'add' ? 'Add New Team Member' : 'Edit Team Member'}
                                        </h1>
                                        <p style={{ color: '#6b7280', marginTop: '4px' }}>
                                            {mode === 'add' ? 'Add a new user to Smart Medical Clinic' : 'Edit user details'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="card mb-4" style={{
                                borderRadius: '16px',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                border: '1px solid #f3f4f6',
                                padding: '32px'
                            }}>
                                <div className="d-flex align-items-center gap-3 mb-6">
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        backgroundColor: '#dbeafe',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <User style={{ width: '20px', height: '20px', color: '#2563eb' }} />
                                    </div>
                                    <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                                        Personal Information
                                    </h2>
                                </div>

                                <div className="row">
                                    {/* Profile Photo */}
                                    <div className="col-lg-3 mb-4 mb-lg-0">
                                        <div className="text-center">
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                <div style={{
                                                    width: '128px',
                                                    height: '128px',
                                                    borderRadius: '16px',
                                                    border: '4px solid #e5e7eb',
                                                    overflow: 'hidden',
                                                    background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    {imagePreview ? (
                                                        <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <User style={{ width: '48px', height: '48px', color: '#9ca3af' }} />
                                                    )}
                                                </div>
                                                <label style={{
                                                    position: 'absolute',
                                                    bottom: '-8px',
                                                    right: '-8px',
                                                    width: '32px',
                                                    height: '32px',
                                                    backgroundColor: '#2563eb',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer'
                                                }}>
                                                    <Camera style={{ width: '16px', height: '16px', color: 'white' }} />
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={handleImageUpload}
                                                    />
                                                </label>
                                            </div>
                                            <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '12px' }}>
                                                Upload profile photo
                                            </p>
                                        </div>
                                    </div>

                                    {/* Form Fields */}
                                    <div className="col-lg-9">
                                        <div className="row">
                                            <div className="col-md-6 mb-4">
                                                <label className="form-label" style={{
                                                    display: 'block',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    marginBottom: '8px'
                                                }}>
                                                    Full Name <RequiredField />
                                                </label>
                                                <div style={{ position: 'relative' }}>
                                                    <User style={{
                                                        position: 'absolute',
                                                        left: '12px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        width: '20px',
                                                        height: '20px',
                                                        color: '#9ca3af'
                                                    }} />
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                                                        value={formData.fullName}
                                                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            paddingLeft: '40px',
                                                            paddingRight: '16px',
                                                            paddingTop: '12px',
                                                            paddingBottom: '12px',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '12px'
                                                        }}
                                                        placeholder="Enter full name"
                                                    />
                                                    {errors.fullName && (
                                                        <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                            {errors.fullName}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-md-6 mb-4">
                                                <label className="form-label" style={{
                                                    display: 'block',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    marginBottom: '8px'
                                                }}>
                                                    Gender <RequiredField />
                                                </label>
                                                <select
                                                    className={`form-select ${errors.gender ? 'is-invalid' : ''}`}
                                                    value={formData.gender}
                                                    onChange={(e) => handleInputChange('gender', e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px'
                                                    }}
                                                >
                                                    <option value="">Select gender</option>
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                                {errors.gender && (
                                                    <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                        {errors.gender}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="col-md-6 mb-4">
                                                <label className="form-label" style={{
                                                    display: 'block',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    marginBottom: '8px'
                                                }}>
                                                    Date of Birth <RequiredField />
                                                </label>
                                                <div style={{ position: 'relative' }}>
                                                    <Calendar style={{
                                                        position: 'absolute',
                                                        left: '12px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        width: '20px',
                                                        height: '20px',
                                                        color: '#9ca3af'
                                                    }} />
                                                    <input
                                                        type="date"
                                                        className={`form-control ${errors.DateofBirth ? 'is-invalid' : ''}`}
                                                        value={formData.DateofBirth}
                                                        onChange={(e) => handleDOBChange(e.target.value)}
                                                        max={new Date().toISOString().split('T')[0]}
                                                        style={{
                                                            width: '100%',
                                                            paddingLeft: '40px',
                                                            paddingRight: '16px',
                                                            paddingTop: '12px',
                                                            paddingBottom: '12px',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '12px'
                                                        }}
                                                    />
                                                    {errors.DateofBirth && (
                                                        <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                            {errors.DateofBirth}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-md-6 mb-4">
                                                <label className="form-label" style={{
                                                    display: 'block',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    marginBottom: '8px'
                                                }}>
                                                    Age
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.age ? 'is-invalid' : ''}`}
                                                    value={formData.age}
                                                    onChange={(e) => handleAgeChange(e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px'
                                                    }}
                                                    placeholder="Enter age"
                                                />
                                                {errors.age && (
                                                    <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                        {errors.age}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="col-md-6 mb-4">
                                                <label className="form-label" style={{
                                                    display: 'block',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    marginBottom: '8px'
                                                }}>
                                                    Mobile Number <RequiredField />
                                                </label>
                                                <div style={{ position: 'relative' }}>
                                                    <Phone style={{
                                                        position: 'absolute',
                                                        left: '12px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        width: '20px',
                                                        height: '20px',
                                                        color: '#9ca3af'
                                                    }} />
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.mobileNumber ? 'is-invalid' : ''}`}
                                                        value={formData.mobileNumber}
                                                        onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                                                        maxLength={10}
                                                        style={{
                                                            width: '100%',
                                                            paddingLeft: '40px',
                                                            paddingRight: '16px',
                                                            paddingTop: '12px',
                                                            paddingBottom: '12px',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '12px'
                                                        }}
                                                        placeholder="Enter 10-digit mobile number"
                                                    />
                                                    {errors.mobileNumber && (
                                                        <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                            {errors.mobileNumber}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Professional Information Section */}
                                <div className="card mb-4" style={{
                                    borderRadius: '16px',
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                    border: '1px solid #f3f4f6',
                                    padding: '32px'
                                }}>
                                    <div className="d-flex align-items-center gap-3 mb-6">
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#d1fae5',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Stethoscope style={{ width: '20px', height: '20px', color: '#059669' }} />
                                        </div>
                                        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                                            Professional Information
                                        </h2>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Qualification
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <GraduationCap style={{
                                                    position: 'absolute',
                                                    left: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    width: '20px',
                                                    height: '20px',
                                                    color: '#9ca3af'
                                                }} />
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.qualification ? 'is-invalid' : ''}`}
                                                    value={formData.qualification}
                                                    onChange={(e) => handleInputChange('qualification', e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        paddingLeft: '40px',
                                                        paddingRight: '16px',
                                                        paddingTop: '12px',
                                                        paddingBottom: '12px',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px'
                                                    }}
                                                    placeholder="e.g., MBBS, MD, Nursing Diploma"
                                                />
                                                {errors.qualification && (
                                                    <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                        {errors.qualification}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                User Role <RequiredField />
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <div style={{
                                                    position: 'absolute',
                                                    left: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    color: '#9ca3af'
                                                }}>
                                                    {getRoleIcon(formData.userRoleId)}
                                                </div>
                                                <select
                                                    className={`form-select ${errors.userRoleId ? 'is-invalid' : ''}`}
                                                    value={formData.userRoleId}
                                                    onChange={(e) => handleInputChange('userRoleId', e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        paddingLeft: '40px',
                                                        paddingRight: '16px',
                                                        paddingTop: '12px',
                                                        paddingBottom: '12px',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px'
                                                    }}
                                                >
                                                    <option value="">Select user role</option>
                                                    {userRoles.map((role) => (
                                                        <option key={role.UserRoleId} value={role.UserRoleId}>
                                                            {role.RoleType}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.userRoleId && (
                                                    <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                        {errors.userRoleId}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Address Information Section */}
                                <div className="card mb-4" style={{
                                    borderRadius: '16px',
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                    border: '1px solid #f3f4f6',
                                    padding: '32px'
                                }}>
                                    <div className="d-flex align-items-center gap-3 mb-6">
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#f3e8ff',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <MapPin style={{ width: '20px', height: '20px', color: '#9333ea' }} />
                                        </div>
                                        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                                            Address Information
                                        </h2>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Address Line 1 <RequiredField />
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <MapPinned style={{
                                                    position: 'absolute',
                                                    left: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    width: '20px',
                                                    height: '20px',
                                                    color: '#9ca3af'
                                                }} />
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.addressLine1 ? 'is-invalid' : ''}`}
                                                    value={formData.addressLine1}
                                                    onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        paddingLeft: '40px',
                                                        paddingRight: '16px',
                                                        paddingTop: '12px',
                                                        paddingBottom: '12px',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px'
                                                    }}
                                                    placeholder="Enter house/flat number, street name"
                                                />
                                                {errors.addressLine1 && (
                                                    <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                        {errors.addressLine1}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Address Line 2
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.addressLine2}
                                                onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '12px'
                                                }}
                                                placeholder="Enter area, landmark (optional)"
                                            />
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                State <RequiredField />
                                            </label>
                                            <select
                                                className={`form-select ${errors.stateId ? 'is-invalid' : ''}`}
                                                value={formData.stateId}
                                                onChange={(e) => handleInputChange('stateId', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '12px'
                                                }}
                                            >
                                                <option value="">Select state</option>
                                                {states.map((state) => (
                                                    <option key={state.StateId} value={state.StateId}>
                                                        {state.StateName}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.stateId && (
                                                <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                    {errors.stateId}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                District <RequiredField />
                                            </label>
                                            <select
                                                className={`form-select ${errors.districtId ? 'is-invalid' : ''}`}
                                                value={formData.districtId}
                                                onChange={(e) => handleInputChange('districtId', e.target.value)}
                                                disabled={!formData.stateId}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '12px',
                                                    backgroundColor: !formData.stateId ? '#f9fafb' : 'white'
                                                }}
                                            >
                                                <option value="">Select district</option>
                                                {districts.map((district) => (
                                                    <option key={district.DistrictId} value={district.DistrictId}>
                                                        {district.DistrictName}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.districtId && (
                                                <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                    {errors.districtId}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Mandal <RequiredField />
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.mandal ? 'is-invalid' : ''}`}
                                                value={formData.mandal}
                                                onChange={(e) => handleInputChange('mandal', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '12px'
                                                }}
                                                placeholder="Enter mandal name"
                                            />
                                            {errors.mandal && (
                                                <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                    {errors.mandal}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                City <RequiredField />
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                                                value={formData.city}
                                                onChange={(e) => handleInputChange('city', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '12px'
                                                }}
                                                placeholder="Enter city name"
                                            />
                                            {errors.city && (
                                                <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                    {errors.city}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Village <RequiredField />
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.village ? 'is-invalid' : ''}`}
                                                value={formData.village}
                                                onChange={(e) => handleInputChange('village', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '12px'
                                                }}
                                                placeholder="Enter village name"
                                            />
                                            {errors.village && (
                                                <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                    {errors.village}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Pincode <RequiredField />
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.pincode ? 'is-invalid' : ''}`}
                                                value={formData.pincode}
                                                onChange={(e) => handleInputChange('pincode', e.target.value)}
                                                maxLength={6}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '12px'
                                                }}
                                                placeholder="Enter 6-digit pincode"
                                            />
                                            {errors.pincode && (
                                                <div className="invalid-feedback" style={{ color: '#ef4444', fontSize: '14px' }}>
                                                    {errors.pincode}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Document Section */}
                                <div className="card mb-4" style={{
                                    borderRadius: '16px',
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                    border: '1px solid #f3f4f6',
                                    padding: '32px'
                                }}>
                                    <div className="d-flex align-items-center gap-3 mb-6">
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#fef3c7',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <FileText style={{ width: '20px', height: '20px', color: '#d97706' }} />
                                        </div>
                                        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                                            Document
                                        </h2>
                                    </div>

                                    <div className="row mb-4" style={{
                                        padding: '16px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        backgroundColor: '#f9fafb'
                                    }}>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Document Type
                                            </label>
                                            {mode === 'edit' && document.documentType ? (
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={document.documentType}
                                                    disabled
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px',
                                                        backgroundColor: '#f9fafb'
                                                    }}
                                                />
                                            ) : (
                                                <select
                                                    className="form-select"
                                                    value={document.documentType}
                                                    onChange={(e) => handleDocumentTypeChange(e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px'
                                                    }}
                                                >
                                                    <option value="">Select document type</option>
                                                    {documentTypes.map((type) => (
                                                        <option key={type.KYCCardTypesId} value={type.KYCCardType}>
                                                            {type.KYCCardType}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label" style={{
                                                display: 'block',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#374151',
                                                marginBottom: '8px'
                                            }}>
                                                Document File
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <label style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'white'
                                                }}>
                                                    <Upload style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                                                    <span style={{ color: '#6b7280' }}>
                                                        {document.preview ? 'Change file' : 'Choose file'}
                                                    </span>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => handleDocumentUpload(e)}
                                                    />
                                                </label>
                                            </div>
                                        </div>

                                        {document.preview && (
                                            <div className="col-12 mt-3">
                                                <div style={{
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    padding: '12px',
                                                    backgroundColor: 'white'
                                                }}>
                                                    <h5 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                                                        Preview
                                                    </h5>
                                                    <img
                                                        src={document.preview}
                                                        alt="Document preview"
                                                        style={{
                                                            maxWidth: '100%',
                                                            maxHeight: '150px',
                                                            borderRadius: '4px'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div className="card" style={{
                                    borderRadius: '16px',
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                    border: '1px solid #f3f4f6',
                                    padding: '24px'
                                }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => navigate(`/smartclinics/details/${smartClinicId}`)}
                                            style={{ padding: '12px 24px', borderRadius: '12px' }}
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            type="submit"
                                            className="btn btn-primary d-flex align-items-center gap-2"
                                            disabled={loading}
                                            style={{
                                                padding: '12px 32px',
                                                borderRadius: '12px',
                                                background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                                                border: 'none',
                                                opacity: loading ? 0.5 : 1,
                                                cursor: loading ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <div style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        border: '2px solid white',
                                                        borderTopColor: 'transparent',
                                                        borderRadius: '50%',
                                                        animation: 'spin 1s linear infinite'
                                                    }} />
                                                    {mode === 'add' ? 'Adding User...' : 'Updating User...'}
                                                </>
                                            ) : (
                                                <>
                                                    <UserPlus style={{ width: '16px', height: '16px' }} />
                                                    {mode === 'add' ? 'Add User' : 'Update User'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    <style jsx>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            </PageWrapper>
        </Layout>
    );
}